import React, { useState } from "react";
import PropTypes from "prop-types";
import { Modal, Form } from "react-bootstrap";
import { usePaymentInputs } from "react-payment-inputs";
import styled, { css } from "styled-components";

import { checkIfTestCard } from "_helpers";

import { ReactComponent as CloseSVG } from "assets/img/Close.svg";
import { ReactComponent as mastercard } from "assets/img/CardMASTERCARD.svg";
import { ReactComponent as visa } from "assets/img/CardVISA.svg";
import { ReactComponent as amex } from "assets/img/CardAMEX.svg";
import { ReactComponent as discover } from "assets/img/CardDISCOVER.svg";
import { ReactComponent as Testcard } from "assets/img/CardTESTCARD.svg";
import { ReactComponent as HideIcon } from "assets/img/Hide.svg";
import { ReactComponent as ShowIcon } from "assets/img/Show.svg";

const images = { mastercard, visa, amex, discover, testcard: Testcard, undefined: Testcard };

import breakpoints from "_constants/breakpoints";

const Header = styled.div`
  margin-top: 29px;
  margin-left: 32px;

  font-family: "Montserrat", sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 47px;
  color: #03589c;

  @media (max-width: ${breakpoints.mobileM}) {
    margin-top: 48px;
    margin-left: auto;
    margin-right: auto;
  }
`;
const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  padding: 0;
  border: none;
  background-color: #ffffff;
`;
const Body = styled(Form)`
  padding: 24px 52px 37px;

  @media (max-width: ${breakpoints.mobileM}) {
    padding: 16px;
  }
`;
const Label = styled(Form.Label)`
  height: 28px;
  margin-left: 8px;
  font-style: normal;
  font-weight: 700;
  font-size: 10px;
  line-height: 28px;
  text-transform: uppercase;
`;
const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  ${({ relative }) =>
    relative &&
    css`
      position: relative;
    `}

  ${({ column }) =>
    column &&
    css`
      flex-direction: column;
    `}

  @media (max-width: ${breakpoints.mobileM}) {
    flex-direction: column;
    margin-bottom: 16px;
  }
`;
const Group = styled(Form.Group)`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`;
const Feedback = styled(Form.Control.Feedback)`
  width: 100%;
`;
const CheckboxContainer = styled.div`
  display: flex;
  gap: 8px;
  margin: 16px 0;
  font-size: 14px;
`;
const Input = styled(Form.Control)`
  display: flex;
  height: 48px;
  flex: 1;
  border: 1px solid #a5aaaf;
  box-sizing: border-box;
  border-radius: 4px;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  color: #394149;
`;
const InputCardNumber = styled(Input)`
  padding: 12px 2px 12px 62px;

  @media (max-width: ${breakpoints.mobileM}) {
    padding: 12px 0px 12px 48px;
  }
`;
const CardSVG = styled.div`
  position: absolute;
  width: 35px;
  height: 24px;
  top: 8px;
  left: 14px;

  @media (max-width: ${breakpoints.mobileM}) {
    top: 9px;
    left: 8px;
  }
`;
const InputContainer = styled.div`
  position: relative;
  width: 40%;
  min-width: 106px;

  @media (max-width: ${breakpoints.mobileM}) {
    width: 100%;
    min-width: auto;
  }

  & input {
    padding-right: 68px !important;
    background-position: right calc(0.375em + 0.1875rem + 30px) center !important;
  }
`;

const ViewButton = styled.button`
  position: absolute;
  box-sizing: border-box;
  top: 8px;
  right: 8px;
  height: 30px;
  width: 30px;
  padding: 0px;
  border: none;
  background: transparent;
  color: #f37217;
  text-decoration: underline;
  font-size: 0.875rem;

  & svg {
    width: 100%;
    height: 100%;
  }
`;
const Select = styled(Form.Select)`
  display: flex;
  height: 48px;
  width: 40%;
  padding: 8px 8px 8px 13px;
  border: 1px solid #a5aaaf;
  box-sizing: border-box;
  border-radius: 4px;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  color: #394149;
  &:not(:last-child) {
    margin-right: 12px;
  }

  @media (max-width: ${breakpoints.mobileM}) {
    width: 100%;
  }
`;
const Footer = styled.div`
  width: 318px;
  margin: 48px 0 0;

  @media (max-width: ${breakpoints.mobileM}) {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    justify-content: center;
  }
`;
const Button = styled.button`
  width: 40%;
  height: 52px;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 24px;
  text-align: center;
  color: #03589c;
  border: 2px solid #ecedf1;
  box-sizing: border-box;
  border-radius: 4px;
  ${({ confirm }) =>
    confirm &&
    css`
      color: #ffffff;
      border: none;
      background-color: #f37217;
    `}
  &:not(:last-child) {
    margin-right: 10px;
  }

  @media (max-width: ${breakpoints.mobileM}) {
    width: 100%;
  }
`;

const initialState = {
  cardNumber: "",
  cardMonth: "",
  cardYear: "",
  cvv: "",
  cardReplacing: false,
  validated: false,
  beforeSubmit: true,
};

export default function AddPaymentMethodModal({ show, primaryId, onCancel, onConfirm }) {
  const [cardNumber, setCardNumber] = useState(initialState.cardNumber);
  const [cardMonth, setCardMonth] = useState(initialState.cardMonth);
  const [cardYear, setCardYear] = useState(initialState.cardYear);
  const [cvv, setCvv] = useState(initialState.cvv);
  const [cardReplacing, setCardReplacing] = useState(initialState.cardReplacing);
  const [showCVV, setShowCVV] = useState(false);
  const [validated, setValidated] = useState(initialState.validated);
  const [beforeSubmit, setBeforeSubmit] = useState(initialState.beforeSubmit);

  const { meta, getCardNumberProps } = usePaymentInputs();
  const { erroredInputs, touchedInputs } = meta;
  const Card = images[meta.cardType?.type];

  const resetState = () => {
    setCardNumber(initialState.cardNumber);
    setCardMonth(initialState.cardMonth);
    setCardYear(initialState.cardYear);
    setCvv(initialState.cvv);
    setCardReplacing(initialState.cardReplacing);
    setValidated(initialState.validated);
    setBeforeSubmit(initialState.beforeSubmit);
  };

  const handleCancel = () => {
    resetState();
    onCancel();
  };

  const getYearDropList = () => {
    const year = new Date().getFullYear();

    return Array.from(new Array(20), (v, i) => (
      <option key={i} value={(year + i) % 100}>
        {year + i}
      </option>
    ));
  };

  const checkDate = () => {
    if (cardYear && cardMonth) {
      const expDate = new Date().setFullYear("20" + cardYear, cardMonth, 1);
      const today = new Date().valueOf();

      return expDate < today;
    }

    return true;
  };

  const handleChangeCardNumber = ({ target: { value } }) => setCardNumber(value);
  const handleChangeCvv = ({ target: { value } }) => setCvv(value.replace(/[^0-9]/g, "").replace(/(\..*)\./g));
  const handleChangeCardReplacing = ({ target: { checked } }) => setCardReplacing(checked);

  const handleShowPassword = () => {
    setShowCVV(!showCVV);
  };

  const handleConfirm = (e) => {
    e.preventDefault();
    setBeforeSubmit(false);
    const section1 = document.querySelector(".is-invalid");

    if (section1 != null) section1.scrollIntoView({ behavior: "smooth", block: "start" });

    if (
      (typeof erroredInputs.cardNumber === "undefined" || checkIfTestCard(cardNumber)) &&
      typeof erroredInputs.cvc === "undefined" &&
      !checkDate()
    ) {
      const form = e.currentTarget;

      if (form.checkValidity() === false) {
        e.preventDefault();
        e.stopPropagation();
        const section = document.querySelector(".form-control:invalid");
        if (section != null) section.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo(0, 0);
        const cardDetails = {
          cardNumber: cardNumber.replace(/\s/g, ""),
          cardType: meta.cardType?.type || "TESTCARD",
          cardMonth,
          cardYear,
          cvv,
          paySourceId: cardReplacing ? primaryId : null,
        };
        onConfirm(cardDetails);
        resetState();
        return;
      }
    }
    setValidated(true);
  };

  return (
    <Modal centered show={show} onHide={handleCancel}>
      <Header>{cardReplacing ? "Replace" : "Add"} a Payment Method</Header>
      <CloseButton onClick={onCancel}>
        <CloseSVG />
      </CloseButton>
      <Body noValidate validated={validated} onSubmit={handleConfirm}>
        <Group>
          <Label>Card number</Label>
          <Row relative="true" column>
            <InputCardNumber
              required
              isInvalid={
                !checkIfTestCard(cardNumber) &&
                ((touchedInputs.cardNumber && erroredInputs.cardNumber) ||
                  ((!cardNumber || cardNumber.length === 0) && !beforeSubmit))
              }
              value={cardNumber}
              placeholder="0000 0000 0000 0000"
              data-threeds="pan"
              {...getCardNumberProps({ onChange: handleChangeCardNumber })}
            />
            <CardSVG>{Card ? <Card /> : <Testcard />}</CardSVG>
            <Feedback type="invalid">Please provide a valid card number.</Feedback>
          </Row>
        </Group>
        <Group>
          <Label>Expiration date</Label>
          <Row>
            <Select
              required
              isInvalid={(touchedInputs.expiryDate && erroredInputs.expiryDate) || (checkDate() && !beforeSubmit)}
              value={cardMonth}
              onChange={({ target: { value } }) => setCardMonth(value)}
            >
              <option value="">Month</option>
              <option value="01">January</option>
              <option value="02">February</option>
              <option value="03">March</option>
              <option value="04">April</option>
              <option value="05">May</option>
              <option value="06">June</option>
              <option value="07">July</option>
              <option value="08">August</option>
              <option value="09">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </Select>
            <Select
              required
              isInvalid={(touchedInputs.expiryDate && erroredInputs.expiryDate) || (checkDate() && !beforeSubmit)}
              value={cardYear}
              onChange={({ target: { value } }) => setCardYear(value)}
            >
              <option value="">Year</option>
              {getYearDropList()}
            </Select>
            <Feedback type="invalid">Please provide a valid date.</Feedback>
          </Row>
        </Group>
        <Group>
          <Label>CVV</Label>
          <InputContainer>
            <Input
              required
              type={showCVV ? "text" : "password"}
              placeholder="CVV"
              name="cvv"
              maxLength="4"
              pattern="^[0-9]{3,4}$"
              value={cvv}
              onChange={handleChangeCvv}
              isInvalid={!beforeSubmit && !cvv}
            />
            <ViewButton type="button" name="new-password" onClick={() => handleShowPassword("new-password")}>
              {showCVV ? <HideIcon className="icon" /> : <ShowIcon className="icon" />}
            </ViewButton>
            <Feedback type="invalid">Please provide a valid CVV.</Feedback>
          </InputContainer>
        </Group>
        <Group>
          <CheckboxContainer>
            <Form.Check
              id="cardReplacing"
              type="checkbox"
              onChange={handleChangeCardReplacing}
              checked={cardReplacing}
            />
            <Form.Label htmlFor="cardReplacing">Replace the Current Card on File</Form.Label>
          </CheckboxContainer>
        </Group>
        <Footer>
          <Button type="reset" onClick={handleCancel}>
            Cancel
          </Button>
          <Button confirm type="submit">
            Confirm
          </Button>
        </Footer>
      </Body>
    </Modal>
  );
}

AddPaymentMethodModal.propTypes = {
  show: PropTypes.bool,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  primaryId: PropTypes.string,
};

AddPaymentMethodModal.defaultProps = {
  primaryId: null,
};
